<template>
  <PageContainer>
    <View direction="column" gap="sm">
      <PageBackNav
        :teleport-slot-id="headerTeleportSlot"
        @back-nav="goBack(portfolioRoute())"
        desktop-back-title="Back"
      />

      <View direction="row" gap="md" align="start" class="profile-container">
        <Card size="medium" class="rail-card">
          <View direction="column" class="rail">
            <View align="center" gap="md" class="user-block">
              <Avatar :user-name="user?.name || '?'" :image-url="user?.avatar" size="medium" />
              <View direction="column" class="user-block-text">
                <Text variant="label-medium" class="user-name">{{ user?.name }}</Text>
                <Text variant="body-small" color="tertiary" class="user-email">{{ user?.email }}</Text>
              </View>
            </View>

            <Divider class="rail-divider" />

            <View direction="column" gap="xs" class="rail-items">
              <button
                v-for="tab in tabs"
                :key="tab.id"
                type="button"
                class="rail-item"
                :class="{ active: activeTab === tab.id }"
                :data-testid="`profile-tab-${tab.id}`"
                @click="setActiveTab(tab.id)"
              >
                <Icon :name="tab.icon" size="small" />
                <Text variant="label-medium">{{ tab.title }}</Text>
              </button>
            </View>

            <Divider class="rail-divider" />

            <button type="button" class="rail-item logout-item" data-testid="profile-log-out" @click="showLogoutModal">
              <Icon name="log-out-03" size="small" />
              <Text variant="label-medium">Log out</Text>
            </button>
          </View>
        </Card>

        <Card size="large" class="content-card">
          <div class="content">
            <component :is="activeTabComponent" ref="activeTabRef" />
          </div>
        </Card>
      </View>
    </View>
  </PageContainer>

  <LogoutModal ref="logoutModalRef" :on-logout-success="openSignIn" />
  <DiscardChangesModal ref="discardModalRef" />
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { onBeforeRouteLeave, onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router';

import { LogoutModal, useAuthStore } from '@tallo/auth';
import { Avatar, Card, Divider, Icon, IconName, PageBackNav, PageContainer, Text, View } from '@tallo/design-system';
import { useInvestorStore } from '@tallo/investor/investor';
import { portfolioRoute, useNavigation } from '@tallo/investor/navigation';

import { headerTeleportSlot } from '../root/header-teleport-slot.const';
import DiscardChangesModal from './modals/discard-changes-modal.vue';
import ProfileCompanyTab from './tabs/profile-company-tab.vue';
import ProfileGeneralTab from './tabs/profile-general-tab.vue';
import ProfilePreferencesTab from './tabs/profile-preferences-tab.vue';
import ProfileSecurityTab from './tabs/profile-security-tab.vue';
import ProfileTeamTab from './tabs/profile-team-tab.vue';

enum ProfileTabId {
  GENERAL = 'general',
  SECURITY = 'security',
  COMPANY = 'company',
  TEAM = 'team',
  PREFERENCES = 'preferences',
}

interface ProfileTab {
  id: ProfileTabId;
  title: string;
  icon: IconName;
  component: unknown;
  /** Company-wide settings only an admin changes */
  adminOnly?: boolean;
}

const allTabs: ProfileTab[] = [
  { id: ProfileTabId.GENERAL, title: 'General', icon: 'user-01', component: ProfileGeneralTab },
  { id: ProfileTabId.SECURITY, title: 'Security', icon: 'lock-01', component: ProfileSecurityTab },
  {
    id: ProfileTabId.COMPANY,
    title: 'Company profile',
    icon: 'building-06',
    component: ProfileCompanyTab,
    adminOnly: true,
  },
  { id: ProfileTabId.TEAM, title: 'Team', icon: 'users-01', component: ProfileTeamTab },
  { id: ProfileTabId.PREFERENCES, title: 'Preferences', icon: 'sliders-04', component: ProfilePreferencesTab },
];

const { goBack, openSignIn } = useNavigation();
const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const investorStore = useInvestorStore();
const { investor, isAdmin } = storeToRefs(investorStore);

const tabs = computed(() => allTabs.filter((tab) => !tab.adminOnly || isAdmin.value));

const user = computed(() => investor.value?.user);

const logoutModalRef = ref<InstanceType<typeof LogoutModal>>();
const discardModalRef = ref<InstanceType<typeof DiscardChangesModal>>();
const activeTabRef = ref<{ isDirty: boolean; reset: () => void }>();

const activeTab = computed<ProfileTabId>(() => {
  const requested = route.query.tab as ProfileTabId;
  return tabs.value.some((tab) => tab.id === requested) ? requested : ProfileTabId.GENERAL;
});

const activeTabComponent = computed(() => tabs.value.find((tab) => tab.id === activeTab.value)?.component);

const activeTabTitle = computed(() => tabs.value.find((tab) => tab.id === activeTab.value)?.title ?? '');

async function setActiveTab(tabId: ProfileTabId) {
  if (tabId === activeTab.value) return;

  if (!(await confirmLeavingDirtyTab())) return;

  router.replace({ query: { ...route.query, tab: tabId } });
}

/** True when it's fine to leave: the tab is clean, or the user chose to discard (which also resets it) */
async function confirmLeavingDirtyTab(): Promise<boolean> {
  if (!activeTabRef.value?.isDirty) return true;

  const discard = await discardModalRef.value!.confirm(activeTabTitle.value);
  if (discard) {
    activeTabRef.value?.reset();
  }

  return discard;
}

onBeforeRouteLeave(async () => {
  // Logging out already tore the session down - blocking navigation would strand the user
  if (!authStore.isAuthenticated) return true;

  return confirmLeavingDirtyTab();
});

// Same-route ?tab changes (browser back/forward, deep links) don't hit onBeforeRouteLeave.
// The rail's own setActiveTab already confirmed-then-reset before it navigates, so by the time
// this fires for a rail click the tab is clean and it passes through without a second prompt.
onBeforeRouteUpdate(async (to, from) => {
  if (!authStore.isAuthenticated || to.query.tab === from.query.tab) return true;

  return confirmLeavingDirtyTab();
});

watch(
  () => route.query,
  (query) => {
    if (!query.tab) {
      router.replace({ query: { ...route.query, tab: ProfileTabId.GENERAL } });
    }
  },
  { immediate: true },
);

function showLogoutModal() {
  logoutModalRef.value?.show();
}
</script>

<style lang="scss" scoped>
@import 'styles/mixins/layout.mixins';

.profile-container {
  flex-direction: column;

  @include screen-min-md {
    flex-direction: row;
  }
}

.rail-card {
  width: 100%;

  @include screen-min-md {
    width: 16rem;
    flex-shrink: 0;
  }
}

.content-card {
  width: 100%;
  flex-grow: 1;
  min-width: 0;
}

.content {
  max-width: 40rem;
}

.user-block {
  padding: var(--t-spacing-sm);
}

.user-block-text {
  min-width: 0;
}

.user-name,
.user-email {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rail-divider {
  margin: var(--t-spacing-sm) 0;
}

.rail-item {
  display: flex;
  align-items: center;
  gap: var(--t-spacing-md);
  width: 100%;
  padding: var(--t-spacing-sm);
  border: 0;
  border-radius: var(--t-border-radius-sm);
  background: transparent;
  color: var(--t-text-color-secondary);
  cursor: pointer;
  text-align: start;
  transition: background-color 150ms ease;

  &:hover {
    background-color: var(--t-color-neutral-50);
  }

  &.active {
    background-color: var(--t-color-neutral-100);
    color: var(--t-text-color-primary);
  }
}

@include screen-max-md {
  // Stacked, the rail becomes a horizontal strip so content starts one line down
  .rail {
    flex-direction: row;
    align-items: center;
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .user-block {
    display: none;
  }

  .rail-items {
    flex-direction: row;
  }

  .rail-item {
    width: auto;
    flex: 0 0 auto;
    white-space: nowrap;
  }

  // Doubled to outweigh the component's own horizontal-rule styling regardless of bundle order
  .rail-divider.rail-divider {
    width: 1px;
    height: 1.5rem;
    flex: 0 0 auto;
    margin: 0 var(--t-spacing-xs);
  }

  // With the user block hidden, its trailing divider would render as a stray leading bar
  .user-block + .rail-divider {
    display: none;
  }
}
</style>
